import React from "react";
import type { ShoppingListItem, SourceId } from "../../types";
import { ALL_SOURCES, SOURCE_LABELS } from "../../types";
import { SourceBadge } from "./SourceBadge";
import { rankUnitPrice } from "../../unit-price";
import { UnitPriceBadge } from "./UnitPriceBadge";

interface Props {
  items: ShoppingListItem[];
  onRemove: (id: string) => void;
  onAddMore: () => void;
  onCompare: () => void;
}

function formatPrice(price: number): string {
  return `R$ ${price.toFixed(2)}`;
}

export function ShoppingList({ items, onRemove, onAddMore, onCompare }: Props) {
  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400 mb-4">Lista vazia</p>
        <button
          onClick={onAddMore}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          Buscar produtos
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Lista de compras ({items.length} itens)
      </h2>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-2 pr-4 font-medium text-gray-700 dark:text-gray-300">
                Item
              </th>
              {ALL_SOURCES.map((source) => (
                <th
                  key={source}
                  className="text-right py-2 px-2 font-medium text-gray-700 dark:text-gray-300"
                >
                  <SourceBadge source={source} />
                </th>
              ))}
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const itemUnitPrices = ALL_SOURCES.map((s) => item.matches[s]?.unitPrice ?? null);
              const itemRanks = rankUnitPrice(itemUnitPrices);
              return (
                <tr key={item.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-4">
                    <p className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
                      {item.primary.name}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">{item.searchTerm}</p>
                  </td>
                  {ALL_SOURCES.map((source, sourceIdx) => {
                    const match = item.matches[source];
                    return (
                      <td
                        key={source}
                        className="text-right py-2 px-2 whitespace-nowrap"
                      >
                        {match ? (
                          <div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {formatPrice(match.price)}
                            </span>
                            {match.unitPrice && (
                              <div className="mt-0.5">
                                <UnitPriceBadge
                                  value={match.unitPrice.value}
                                  unit={match.unitPrice.unit}
                                  rank={itemRanks[sourceIdx]}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">---</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-2 pl-2">
                    <button
                      onClick={() => onRemove(item.id)}
                      className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                      title="Remover"
                    >
                      x
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={onAddMore}
          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
        >
          Adicionar mais
        </button>
        <button
          onClick={onCompare}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 font-medium"
        >
          Comparar totais
        </button>
      </div>
    </div>
  );
}
